import { AmountDto, CheckoutService, PaymentMethodDto, ValidationProblemDetails } from "../api";
import { StripeElements, StripePaymentElement } from "@stripe/stripe-js";
import { getOrderStore, getStripeStore } from "../utils";
import { AlpineComponent } from "@nxtlvlsoftware/alpine-typescript/";
import { NotificationData } from "../notifier.component.types";

// This component handles the traditional checkout flow
export class CheckoutPaymentHandlerComponent extends AlpineComponent {
    private stripeElements?: StripeElements | null = null;
    private stripePaymentMethodElement?: StripePaymentElement;
    private loadingTimeoutHandle: number | null | undefined;

    public paymentFormParameters: {
        [key: string]: string | null;
    } = {};
    public paymentFormMethod: string = "POST";
    public paymentFormAction: string = "";

    public stripePaymentMethodMounted = false;
    public allErrors?: Record<string, string[]> = {};
    public loading = false;
    public shippingMethodsLoading = false;

    constructor() {
        super();
    }

    async init(){
        console.debug("Initializing Checkout Payment Handler Component");
        //await this.trySetupStripe();

        this.$watch("$store.order.transactionAmount", async (newValue, oldValue) => {
            await this.handleTransactionAmountChanged(oldValue as AmountDto, newValue as AmountDto);
        });
        this.$watch("$store.order.pendingShippingOptionUpdate", (newValue, _oldValue) => {
            this.shippingMethodsLoading = newValue as boolean;
        });

        this.$watch("$store.stripe.stripeReady", (newValue) => {
            if(newValue === true) {
                this.setupElement();
                this.mount();
            }
        });

        this.$watch("$store.order.selectedPaymentMethod", (newValue, oldValue) => {
            const oldPaymentMethod = oldValue as PaymentMethodDto | null | undefined;
            const newPaymentMethod = newValue as PaymentMethodDto | null | undefined;
            this.handlePaymentMethodChanged(oldPaymentMethod, newPaymentMethod);
        });
    }

    public async handleTransactionAmountChanged(oldValue: AmountDto | null | undefined, newValue?: AmountDto | null | undefined){
        if(oldValue?.value === newValue?.value){
            return;
        }

        // If the amount has changed to zero, Stripe can't be used as a payment method.
        if(newValue?.value === 0) {
            console.debug("Unmounting stripe elements as transaction amount is zero.");
            if(this.stripePaymentMethodElement){
                this.stripePaymentMethodElement.unmount();
                this.stripePaymentMethodMounted = false;
            }
        }
    }
    public async handlePaymentMethodChanged(oldValue: PaymentMethodDto | null | undefined, newValue: PaymentMethodDto | null | undefined){
        if(oldValue?.alias === "stripePayments" && newValue?.alias !== "stripePayments"){
            // Unmount stripe elements
            console.debug("Unmounting stripe elements as payment method changed.");
            if(this.stripePaymentMethodElement){
                this.stripePaymentMethodElement.unmount();
                this.stripePaymentMethodMounted = false;
            }
        }
        if(newValue?.alias === "stripePayments" && oldValue?.alias !== "stripePayments"){
            // Mount stripe elements
            console.debug("Mounting stripe elements as payment method changed.");
            this.setupElement();
            this.mount();
        }
    }

    // Public Utils
    public selectedPaymentMethodMatchesAlias(alias: string): boolean {
        return getOrderStore().selectedPaymentMethod?.alias === alias;
    }

    setupElement(){
        const stripeStore = getStripeStore();
        this.stripeElements = stripeStore.getStripeElements();
        if(stripeStore.stripeReady && this.stripeElements && !this.stripePaymentMethodElement){
            this.stripePaymentMethodElement = this.stripeElements.create(
                'payment',
                {
                    fields: {
                        billingDetails: {
                            name: 'never',
                            email: 'never',
                            phone: 'never',
                            address: 'never',
                        }
                    }
                });
            this.stripePaymentMethodElement.on('ready', this.handleStripePaymentMethodReady.bind(this));
        }
    }

    mount() {
        if(this.stripePaymentMethodElement && getStripeStore().stripeReady && !this.stripePaymentMethodMounted && this.selectedPaymentMethodMatchesAlias("stripePayments")){
            this.stripePaymentMethodElement.mount(this.$refs["stripe-payment-method-container"]);
            this.stripePaymentMethodMounted = true;
        }
    }

    handleStripePaymentMethodReady() {
        console.debug("Stripe Payment Method Element is ready.");
    }


    public async handlePaymentFormSubmit() {
        this.beginLoading();
        const order = getOrderStore();
        const stripeStore = getStripeStore();

        // If using Stripe Payments, submit the Stripe elements first.
        if(this.selectedPaymentMethodMatchesAlias("stripePayments") && this.stripeElements && this.stripePaymentMethodMounted){
            const stripeSumitResult = await this.stripeElements!.submit();
            if(stripeSumitResult.error){
                this.$dispatch("notify", {
                    type: 'error',
                    message: `${stripeSumitResult.error.message}`,
                } as NotificationData);
                this.endLoading();
                return;
            }
        }

        const { data, error } = await CheckoutService.postInitPayment({
            path:{ storeId: order.storeId },
        });


        if(error || !data || !data.method || data.method == "Invalid"){
            console.error("Error initializing payment on server:", error);
            if(error) {
                window.appInsights?.trackTrace({ message: "Error initializing payment on server", severityLevel: 3, properties: { error: JSON.stringify(error) } });
            }
            this.$dispatch("notify", {
                type: 'error',
                message: this.getErrorMessage(error) ?? "An error occurred initializing the payment.",
            } as NotificationData);
            this.endLoading();
            return;
        }

        // If using stripe, confirm the payment directly via the Stripe SDK.
        if(data.method == "StripeConfirm" && data.parameters && data.parameters["clientSecret"] && data.parameters["returnUrl"]){
            const { error: confirmError } = await stripeStore.stripe!.confirmPayment({
                clientSecret: data.parameters["clientSecret"],
                confirmParams: {
                    return_url: data.parameters["returnUrl"],
                    payment_method_data: {
                        billing_details: {
                            address: {
                                line1: order.billingInformation.addressLine1 ?? "",
                                line2: order.billingInformation.addressLine2 ?? "",
                                city: order.billingInformation.city ?? "",
                                state: "",
                                postal_code: order.billingInformation.postcode ?? "",
                                country: order.billingInformation.country?.alias.toLowerCase() ?? "gb",
                            },
                            email: order.billingInformation?.email ?? order.customerInformation.email ?? "",
                            name: (order.billingInformation.firstName ?? "") + " " + (order.billingInformation.lastName ?? ""),
                            phone: order.billingInformation.phoneNumber ?? "",
                        },
                    },
                    shipping: order.shippingInformation.isSameAsBilling ?
                    {
                        // Same as Billing
                        address: {
                                line1: order.billingInformation.addressLine1 ?? "",
                                line2: order.billingInformation.addressLine2 ?? "",
                                city: order.billingInformation.city ?? "",
                                postal_code: order.billingInformation.postcode ?? "",
                                country: order.billingInformation.country?.alias.toLowerCase() ?? "gb",
                            },
                            name: (order.billingInformation.firstName ?? "") + " " + (order.billingInformation.lastName ?? ""),
                            phone: order.billingInformation.phoneNumber ?? "",
                    }
                    :
                    {
                        // Different shipping address
                        address: {
                            line1: order.shippingInformation.addressLine1 ?? "",
                            line2: order.shippingInformation.addressLine2 ?? "",
                            city: order.shippingInformation.city ?? "",
                            postal_code: order.shippingInformation.postcode ?? "",
                            country: order.shippingInformation.country?.alias.toLowerCase() ?? "gb",
                        },
                        name: (order.shippingInformation.firstName ?? "") + " " + (order.shippingInformation.lastName ?? ""),
                        phone: order.shippingInformation.phoneNumber ?? "",
                    }
                },
                elements: this.stripeElements!,
            });

            if(confirmError){
                console.error("Error confirming Stripe payment:", confirmError);
                this.$dispatch("notify", {
                    type: 'error',
                    message: `${confirmError.message}`,
                } as NotificationData);
            }
            this.endLoading();
            return;
        }

        // Otherwise, submit the payment form normally.
        if(!data.url){return;}

        this.paymentFormAction = data.url;
        if(data.method == "FormGet"){
            this.paymentFormMethod = "GET";
        } else if(data.method == "FormPost"){
            this.paymentFormMethod = "POST";
        }
        this.paymentFormParameters = data.parameters ?? {};

        await this.$nextTick();
        (this.$refs["paymentForm"] as HTMLFormElement).submit();
        this.endLoading();
    }

    private getErrorMessage(error?: ValidationProblemDetails): string | null {
        if(!error || !error.errors){
            return null;
        }
        let message = "";
        for (const errors of Object.values(error.errors)) {
            message += errors.join(", ") + " ";
        }
        return message.trim();
    }

    private beginLoading() {
        if(this.loadingTimeoutHandle) {
            clearTimeout(this.loadingTimeoutHandle);
        }
        this.loading = true;
    }
    private endLoading() {
        this.loadingTimeoutHandle = window.setTimeout(() => {
            this.loading = false;
            this.loadingTimeoutHandle = undefined;
        }, 250);
    }


    /* Bindings */


    protected paymentForm = this.binding({
        ["x-ref"]:"paymentForm",
        ["x-bind:action"]:"paymentFormAction",
        ["x-bind:method"]:"paymentFormMethod",
    })
    protected payNowButton = this.binding({
        ["@click.prevent"](){
            this.handlePaymentFormSubmit();
        },
        [":disabled"](){ return !getOrderStore().isStateValid || this.loading; }
    });
}